import { Plus, Minus, ArrowLeftRight, FileText, FileX, Inbox, Landmark, CircleCheck, CircleX, Lock } from "lucide-react";
import { useNavigate } from "@/hooks/useDeskNavigate";
import UserInitials from "@/components/common/UserInitials";
import { fmtDA } from "@/components/treso/tresoShared";

const MONO = { fontFamily: "'JetBrains Mono', Consolas, monospace" };
const d2 = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—");
const dfull = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const Sep = () => <span aria-hidden style={{ borderLeft: "1px dashed #2E2E35", margin: "10px 0", alignSelf: "stretch" }} />;
/** iter477 59.2 a — bande 40 px : Entrée / Sortie ; autre type (transfert) : même modèle, couleur neutre proposée dans le rapport, non appliquée. */
const BAND = { entree: { bg: "#16A34A", Icon: Plus, label: "ENTRÉE", fg: "#DCFCE7" }, sortie: { bg: "#DC2626", Icon: Minus, label: "SORTIE", fg: "#FEE2E2" }, transfert: { bg: "#3A3A42", Icon: ArrowLeftRight, label: "TRANSF.", fg: "#E6E6EA" } };
/** 59.2 i — mêmes icônes et couleurs que les filtres de statut d'Encaissements. */
const STEPS = [["recu", "Reçu", Inbox, "#60A5FA"], ["depose", "Déposé", Landmark, "#F59E0B"], ["encaisse", "Encaissé", CircleCheck, "#22C55E"], ["verrouille", "Verrouillé", Lock, "#FB923C"]];
const ORDER = ["recu", "depose", "encaisse", "verrouille"];

/** Dates des étapes depuis l'historique des statuts (automatiques, non modifiables) : state:xxx · lock · create (= Reçu). */
export const stepDates = (m) => {
  const out = {};
  for (const h of m.history || []) {
    if (h.action === "create" && !out.recu) out.recu = h.at;
    const st = /^state:(\w+)$/.exec(h.action || "")?.[1];
    if (st && !out[st]) out[st] = h.at;
    if (h.action === "lock") out.verrouille = h.at;
  }
  if (!out.recu && (m.state || m.created_at)) out.recu = m.created_at;
  if (m.locked && !out.verrouille) out.verrouille = m.locked_at || m.updated_at;
  return out;
};

function Journey({ m, testId }) {
  const dates = stepDates(m);
  const reached = m.locked ? 3 : m.state === "rejete" ? ORDER.indexOf((m.steps || []).filter((s) => s.state !== "rejete").slice(-1)[0]?.state || "recu") : Math.max(0, ORDER.indexOf(m.state || "recu"));
  const rejectAt = reached + 1;
  return (
    <div className="flex items-center shrink-0" data-testid={testId}>
      {STEPS.map(([k, label, Icon, color], i) => {
        const isReject = m.state === "rejete" && i === rejectAt;
        const on = isReject || i <= reached;
        const Ic = isReject ? CircleX : Icon, col = isReject ? "#EF4444" : color;
        const date = isReject ? dates.rejete : dates[k];
        return (
          <span key={k} className="flex items-center">
            {i > 0 && <span aria-hidden style={{ width: 6, height: 1, background: "#3A3A42" }} />}
            <span className="flex flex-col items-center justify-center" style={{ width: 40, gap: 1 }} title={`${isReject ? "Rejeté" : label}${date ? " · " + dfull(date) : ""}`} data-testid={`${testId}-${isReject ? "rejete" : k}`} data-on={on}>
              <Ic size={14} style={{ color: on ? col : "#4A4A55" }} />
              <span style={{ fontSize: 9, color: on ? col : "#5A5A65", lineHeight: "10px", whiteSpace: "nowrap" }}>{isReject ? "Rejeté" : label}</span>
              <span className="tabular-nums" style={{ fontSize: 9.5, color: on && date ? "#E6E6EA" : "#5A5A65", lineHeight: "11px" }}>{on && date ? d2(date) : "—"}</span>
            </span>
          </span>);
      })}
    </div>
  );
}

/** iter479 65.1 f — transfert : une seule étape « Effectué », automatique (date de création), non modifiable. */
function DoneStep({ m, testId }) {
  const at = stepDates(m).recu || m.created_at;
  return (
    <div className="flex items-center shrink-0" data-testid={testId}>
      <span className="flex flex-col items-center justify-center" style={{ width: 40, gap: 1 }} title={`Effectué${at ? " · " + dfull(at) : ""}`} data-testid={`${testId}-effectue`} data-on>
        <CircleCheck size={15} style={{ color: "#22C55E" }} />
        <span style={{ fontSize: 9, color: "#22C55E", lineHeight: "10px", whiteSpace: "nowrap" }}>Effectué</span>
        <span className="tabular-nums" style={{ fontSize: 9.5, color: "#E6E6EA", lineHeight: "11px" }}>{d2(at)}</span>
      </span>
    </div>
  );
}

function Docs({ docs = [], testId }) {
  const navigate = useNavigate();
  const empty = !docs.length;
  return (
    <div className="flex items-center shrink-0" style={{ gap: 8, minWidth: 110 }} data-testid={testId}>
      <span className="flex flex-col items-center justify-center shrink-0" style={{ width: 28, height: 36, borderRadius: 6, background: empty ? "#3A3A42" : "#3568B0", gap: 1 }}>
        {empty ? <FileX size={14} color="#9A9AA5" /> : <FileText size={14} color="#fff" />}<span className="tabular-nums" style={{ fontSize: 10, color: "#fff", lineHeight: "11px" }}>{docs.length}</span>
      </span>
      {empty ? <span style={{ fontSize: 11, color: "#6A6A75" }}>Aucun</span> : (
        <span className="flex flex-col" style={{ gap: 1 }}>
          {docs.slice(0, 2).map((d) => <button key={d.id} type="button" onClick={() => navigate(`/documents/${d.id}`)} title={d.title || d.reference} style={{ ...MONO, fontSize: 11, color: "#7FA2E8", textDecoration: "underline", textUnderlineOffset: 2, background: "transparent", padding: 0, textAlign: "left" }} data-testid={`${testId}-open-${d.id}`}>{d.reference || "—"}</button>)}
          {docs.length > 2 && <button type="button" onClick={() => navigate(`/documents/${docs[2].id}`)} title={docs.slice(2).map((d) => d.reference).join(", ")} style={{ fontSize: 10.5, color: "#7FA2E8", background: "transparent", padding: 0, textAlign: "left" }} data-testid={`${testId}-more`}>+{docs.length - 2}</button>}
        </span>)}
    </div>
  );
}

/** iter477 59 — ligne Mouvements en LECTURE SEULE (carte rail) : bande type · créé par · tiers · compte · montant · documents liés · parcours. */
export default function MovementRowCard({ m, tiersTypeLabel }) {
  const band = BAND[m.type] || BAND.transfert;
  const sign = m.type === "entree" ? "+" : m.type === "sortie" ? "−" : "";
  const amountColor = m.type === "entree" ? "#22C55E" : m.type === "sortie" ? "#EF4444" : "#E6E6EA";
  const sub = [tiersTypeLabel, m.category_label, m.project_name].filter(Boolean).join(" · ");
  const isTransfer = m.type === "transfert";
  const account = isTransfer ? `${m.account_label || "—"} → ${m.account_to_label || "—"}` : m.account_label || "—";
  const shownDate = isTransfer ? (m.value_date || m.date) : m.date;
  return (
    <div className="flex items-stretch" style={{ border: "1px solid #2A2A30", borderRadius: 10, background: "#141418", overflow: "hidden", minHeight: 58, opacity: m.state === "rejete" ? .75 : 1 }} data-testid={`mv-row-${m.id}`}>
      <div className="flex flex-col items-center justify-center shrink-0" style={{ width: 40, background: band.bg, gap: 2 }} data-testid={`mv-band-${m.id}`}><band.Icon size={17} color="#fff" strokeWidth={2.4} /><span style={{ fontSize: 8, letterSpacing: ".05em", color: band.fg, fontWeight: 700 }}>{band.label}</span></div>
      <div className="flex flex-col items-center justify-center shrink-0" style={{ width: 54, gap: 1 }} title={`${m.created_by_name || "—"} · ${dfull(m.created_at)}`} data-testid={`mv-created-${m.id}`}>
        <UserInitials user={{ id: m.created_by, first_name: (m.created_by_name || "").split(" ")[0], last_name: (m.created_by_name || "").split(" ").slice(1).join(" ") }} seed={m.created_by} size={24} ring="#141418" />
        <span style={{ fontSize: 9, color: "#5A5A65", lineHeight: "10px" }}>Créé</span><span className="tabular-nums" style={{ fontSize: 9.5, color: "#E6E6EA", lineHeight: "11px" }}>{d2(m.created_at)}</span>
      </div>
      <Sep />
      <div className="flex flex-col justify-center min-w-0" style={{ flex: "1.1 1 0", padding: "0 10px", gap: 2 }}>
        <span className="truncate" style={{ fontSize: 12, color: "#E6E6EA" }} data-testid={`mv-tiers-${m.id}`}>{m.tiers_name || "—"}</span>
        <span className="truncate" style={{ fontSize: 11, color: "#8A8A95" }}>{sub || "—"}</span>
      </div>
      <div className="flex flex-col justify-center min-w-0" style={{ flex: "1.2 1 0", padding: "0 10px", gap: 2 }}>
        <span className="truncate" style={{ fontSize: 12, color: "#E6E6EA" }} data-testid={`mv-account-${m.id}`}>{account}</span>
        <span className="truncate" style={{ fontSize: 11, color: "#8A8A95" }}>{m.mode_label || (isTransfer ? "Transfert" : "—")}{m.reference && <> · <span style={{ ...MONO, fontSize: 10 }}>{m.reference}</span></>}{shownDate && <> · {new Date(shownDate).toLocaleDateString("fr-FR")}</>}</span>
      </div>
      <div className="flex items-center justify-end shrink-0 tabular-nums" style={{ width: 150, padding: "0 10px", fontSize: 13, fontWeight: 500, color: amountColor }} data-testid={`mv-amount-${m.id}`}>{sign}{fmtDA(m.amount)}</div>
      <Docs docs={m.linked_documents} testId={`mv-docs-${m.id}`} />
      <Sep />
      <div className="flex items-center shrink-0" style={{ padding: "0 10px" }}>{isTransfer ? <DoneStep m={m} testId={`mv-journey-${m.id}`} /> : <Journey m={m} testId={`mv-journey-${m.id}`} />}</div>
    </div>
  );
}
